<script setup>
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const alert = useAlert();

const isApplying = ref(false);
const presetKey = ref('global_en');

const PRESETS = {
  global_en: {
    name: 'Global (English)',
    welcome_banner: {
      en: 'Welcome! Support here — happy to help.\n\nTell me what you need help with:\n- Deposit / top-up\n- Withdraw\n- Promo / bonus\n- Can’t login\n\n(For security: don’t share password/OTP/card details.)',
    },
    recommended_intents: ['deposit', 'withdraw', 'bonus_claim'],
  },
  thailand_th: {
    name: 'Thailand (Thai)',
    welcome_banner: {
      th: 'สวัสดีค่ะ 😊 น้องแอดมินยินดีช่วยเหลือนะคะ\n\nติดเรื่องไหนคะ:\n- ฝากไม่เข้า / เติมเงิน\n- ถอนเงิน / ถอนช้า\n- โปรโมชั่น / รับโบนัส\n- เข้าเล่นไม่ได้ / ลิงก์ทางเข้า\n- ลืมรหัส / OTP ไม่มา',
      th_chat:
        'สวัสดีค่าา 😊 น้องแอดมินยินดีช่วยน้า\n\nติดเรื่องไหนคะ:\n- ฝากไม่เข้า / เติมเงิน\n- ถอนเงิน / ถอนช้า\n- โปร / รับโบนัส\n- เข้าเล่นไม่ได้ / ขอทางเข้า\n- ลืมรหัส / OTP ไม่มา',
    },
    recommended_intents: ['bonus_claim'],
  },
  malaysia_ms: {
    name: 'Malaysia (Malay)',
    welcome_banner: {
      ms: 'Hai boss! Support sini — sy boleh bantu.\n\nNak bantu pasal apa?\n- deposit/topup\n- withdraw\n- promo/bonus\n- tak boleh login',
      ms_short: 'Hai boss! Nak bantu apa (depo/wd/promo/login)?',
      en: 'Hi! Support here — happy to help.',
    },
    recommended_intents: ['bonus_claim'],
  },
  indonesia_id: {
    name: 'Indonesia (Indonesian)',
    welcome_banner: {
      id: 'Halo! Customer support di sini — saya siap membantu.\n\nButuh bantuan apa?\n- Deposit/top up\n- Withdraw\n- Promo/bonus\n- Tidak bisa login',
      en: 'Hi! Support here — happy to help.',
    },
    recommended_intents: ['bonus_claim'],
  },
  vietnam_vi: {
    name: 'Vietnam (Vietnamese)',
    welcome_banner: {
      vi: 'Xin chào! Bộ phận hỗ trợ ở đây — mình sẵn sàng giúp bạn.\n\nBạn cần hỗ trợ gì?\n- Nạp tiền\n- Rút tiền\n- Khuyến mãi/bonus\n- Không đăng nhập được',
      en: 'Hi! Support here — happy to help.',
    },
    recommended_intents: ['bonus_claim'],
  },
  chinese_zh: {
    name: 'Chinese',
    welcome_banner: {
      zh: '你好！客服在这里，随时为你服务。\n\n请问你需要帮助哪一项？\n- 充值\n- 提现\n- 优惠/奖金\n- 无法登录',
      en: 'Hi! Support here — happy to help.',
    },
    recommended_intents: ['bonus_claim'],
  },
  tamil_ta: {
    name: 'Tamil',
    welcome_banner: {
      ta: 'வணக்கம்! Customer support இங்கே — உங்களுக்கு உதவ தயாராக உள்ளேன்.\n\nஎதை பற்றி உதவி வேண்டும்?\n- Deposit/Top up\n- Withdraw\n- Promo/Bonus\n- Login பிரச்சனை',
      en: 'Hi! Support here — happy to help.',
    },
    recommended_intents: ['bonus_claim'],
  },
};

const wizard = reactive({
  brandName: '',
  brandKey: '',
  country: 'Global',
  preferredLanguage: 'English',
  links: {
    entry: '',
    signup: '',
    deposit: '',
    withdraw: '',
    promo: '',
    support: '',
  },
});

const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig'] || {});
const assistantId = computed(() => Number(props.assistant?.id) || null);
const selectedPreset = computed(() => PRESETS[presetKey.value] || PRESETS.global_en);

const safeToString = value => {
  if (value === null || value === undefined) return '';
  try {
    return String(value);
  } catch {
    return '';
  }
};

const slugifyBrandKey = input => {
  const s = safeToString(input).trim().toLowerCase();
  if (!s) return '';
  return s
    .replace(/https?:\/\//g, '')
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
};

const safeHost = urlStr => {
  try {
    const u = new URL(safeToString(urlStr));
    return (u.host || '').toLowerCase();
  } catch {
    const s = safeToString(urlStr).trim().toLowerCase();
    if (!s || s.includes('://')) return '';
    return s.split('/')[0];
  }
};

const deriveBrandKey = () => {
  const explicit = slugifyBrandKey(wizard.brandKey);
  if (explicit) return explicit;
  const fromName = slugifyBrandKey(wizard.brandName);
  if (fromName) return fromName;
  return slugifyBrandKey(safeHost(wizard.links.entry));
};

const derived = computed(() => {
  const brandKey = deriveBrandKey();
  const hosts = [
    wizard.links.entry,
    wizard.links.signup,
    wizard.links.deposit,
    wizard.links.withdraw,
    wizard.links.promo,
    wizard.links.support,
  ]
    .map(safeHost)
    .filter(Boolean);
  const allowedDomains = Array.from(new Set(hosts));
  const stripTerms = Array.from(new Set([brandKey, ...allowedDomains])).filter(
    Boolean
  );
  return { brandKey, allowedDomains, stripTerms };
});

const languageToIso = langName => {
  const s = safeToString(langName).toLowerCase();
  if (s.includes('thai')) return 'th';
  if (s.includes('malay')) return 'ms';
  if (s.includes('indonesian')) return 'id';
  if (s.includes('vietnam')) return 'vi';
  if (s.includes('chinese')) return 'zh';
  if (s.includes('tamil')) return 'ta';
  return 'en';
};

const openTab = tab => {
  router.replace({ query: { ...(route.query || {}), tab } });
};

const upsertOfficialLinks = async nextAssistantId => {
  await store.dispatch('aiCloser/fetchLinks', {
    assistantId: nextAssistantId,
    params: { limit: 200, offset: 0 },
  });
  const existing = store.getters['aiCloser/getLinks'] || [];
  const iso = languageToIso(wizard.preferredLanguage);
  const country = safeToString(wizard.country);
  const payloads = [
    { link_type: 'entry', title: 'Entry', url: wizard.links.entry },
    { link_type: 'signup', title: 'Signup', url: wizard.links.signup },
    { link_type: 'deposit', title: 'Deposit', url: wizard.links.deposit },
    { link_type: 'withdraw', title: 'Withdraw', url: wizard.links.withdraw },
    { link_type: 'promo', title: 'Promotions', url: wizard.links.promo },
    { link_type: 'support', title: 'Support', url: wizard.links.support },
  ]
    .map(link => ({ ...link, url: safeToString(link.url).trim() }))
    .filter(link => link.url);

  for (const link of payloads) {
    const found = existing.find(
      item => (item.link_type || item.linkType) === link.link_type
    );
    const data = {
      link_type: link.link_type,
      title: link.title,
      url: link.url,
      enabled: true,
      priority: 100,
      country,
      language: iso,
    };
    if (found?.id) {
      await store.dispatch('aiCloser/updateLink', {
        assistantId: nextAssistantId,
        linkId: found.id,
        data,
      });
    } else {
      await store.dispatch('aiCloser/createLink', {
        assistantId: nextAssistantId,
        data,
      });
    }
  }
};

const applyQuickStart = async () => {
  if (!assistantId.value) return;
  const brandKey = deriveBrandKey();
  if (!brandKey) {
    alert('Add a brand name, brand key, or entry link first.');
    return;
  }

  isApplying.value = true;
  try {
    const existingPromptSections = aiCloserConfig.value?.prompt_sections || {};
    const existingTextSafety = existingPromptSections.text_safety || {};
    const existingStripTerms = Array.isArray(existingTextSafety.strip_terms)
      ? existingTextSafety.strip_terms
      : [];
    const stripTerms = Array.from(
      new Set([
        ...existingStripTerms.map(term => safeToString(term).trim()),
        ...derived.value.stripTerms,
      ])
    ).filter(Boolean);
    const welcomeBanner = selectedPreset.value.welcome_banner || {};

    await store.dispatch('aiCloser/updateConfig', {
      assistantId: assistantId.value,
      data: {
        country: wizard.country,
        preferred_language: wizard.preferredLanguage,
        prompt_sections: {
          ...existingPromptSections,
          brand_key: brandKey,
          welcome_banner: Object.keys(welcomeBanner).length ? welcomeBanner : null,
          text_safety: stripTerms.length
            ? { ...existingTextSafety, strip_terms: stripTerms }
            : null,
        },
      },
    });

    await upsertOfficialLinks(assistantId.value);
    alert('Quick Start applied. Next: review Guidance, Procedures, and Settings.');
  } catch (error) {
    alert(error?.response?.data?.error || error?.message || 'Failed to apply Quick Start');
  } finally {
    isApplying.value = false;
  }
};
</script>

<template>
  <div class="quick-start-card">
    <div class="quick-start-header">
      <div>
        <div class="quick-start-title">Quick Start</div>
        <div class="quick-start-note">
          Use this once to seed brand basics, welcome copy, and official links.
          Procedures are no longer auto-written from here.
        </div>
      </div>
      <div class="quick-start-actions">
        <Button
          size="sm"
          color="slate"
          variant="outline"
          label="Open Guidance"
          @click="openTab('train_guidance')"
        />
        <Button
          size="sm"
          color="slate"
          variant="outline"
          label="Open Procedures"
          @click="openTab('train_procedures')"
        />
      </div>
    </div>

    <div class="quick-start-grid">
      <label class="quick-start-field">
        <span class="quick-start-label">Brand name</span>
        <input
          v-model="wizard.brandName"
          class="quick-start-input"
          placeholder="e.g. Galaxy Support"
        />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Brand key</span>
        <input
          v-model="wizard.brandKey"
          class="quick-start-input"
          placeholder="Optional override"
        />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Country</span>
        <input
          v-model="wizard.country"
          class="quick-start-input"
          placeholder="e.g. Malaysia"
        />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Preferred language</span>
        <select v-model="wizard.preferredLanguage" class="quick-start-input">
          <option value="English">English</option>
          <option value="Thai">Thai</option>
          <option value="Malay">Malay</option>
          <option value="Indonesian">Indonesian</option>
          <option value="Vietnamese">Vietnamese</option>
          <option value="Chinese">Chinese</option>
          <option value="Tamil">Tamil</option>
        </select>
      </label>
      <label class="quick-start-field quick-start-field--full">
        <span class="quick-start-label">Preset</span>
        <select v-model="presetKey" class="quick-start-input">
          <option
            v-for="(preset, key) in PRESETS"
            :key="key"
            :value="key"
          >
            {{ preset.name }}
          </option>
        </select>
      </label>
    </div>

    <div class="quick-start-links">
      <label class="quick-start-field">
        <span class="quick-start-label">Entry URL</span>
        <input v-model="wizard.links.entry" class="quick-start-input quick-start-input--mono" />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Signup URL</span>
        <input v-model="wizard.links.signup" class="quick-start-input quick-start-input--mono" />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Deposit URL</span>
        <input v-model="wizard.links.deposit" class="quick-start-input quick-start-input--mono" />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Withdraw URL</span>
        <input v-model="wizard.links.withdraw" class="quick-start-input quick-start-input--mono" />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Promo URL</span>
        <input v-model="wizard.links.promo" class="quick-start-input quick-start-input--mono" />
      </label>
      <label class="quick-start-field">
        <span class="quick-start-label">Support URL</span>
        <input v-model="wizard.links.support" class="quick-start-input quick-start-input--mono" />
      </label>
    </div>

    <div class="quick-start-preview">
      <div class="quick-start-preview-card">
        <span class="quick-start-label">Generated brand key</span>
        <code>{{ derived.brandKey || '—' }}</code>
      </div>
      <div class="quick-start-preview-card">
        <span class="quick-start-label">Allowed domains preview</span>
        <code>{{ derived.allowedDomains.join(', ') || '—' }}</code>
      </div>
      <div class="quick-start-preview-card">
        <span class="quick-start-label">Strip terms preview</span>
        <code>{{ derived.stripTerms.join(', ') || '—' }}</code>
      </div>
      <div class="quick-start-preview-card">
        <span class="quick-start-label">Recommended procedures</span>
        <code>{{ selectedPreset.recommended_intents.join(', ') || 'none' }}</code>
      </div>
    </div>

    <div class="quick-start-footer">
      <div class="quick-start-note">
        This writes brand basics, welcome banner, strip terms, and official links.
        It does not create procedures automatically anymore.
      </div>
      <Button
        size="sm"
        :is-loading="isApplying"
        :disabled="isApplying"
        label="Apply Quick Start"
        @click="applyQuickStart"
      />
    </div>
  </div>
</template>

<style scoped>
.quick-start-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quick-start-header,
.quick-start-footer {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.quick-start-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.quick-start-title {
  font-size: 15px;
  line-height: 22px;
  font-weight: 700;
  color: #111827;
}

.quick-start-note {
  font-size: 13px;
  line-height: 18px;
  color: #6b7280;
}

.quick-start-grid,
.quick-start-links {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.quick-start-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quick-start-field--full {
  grid-column: 1 / -1;
}

.quick-start-label {
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #475569;
}

.quick-start-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  line-height: 20px;
}

.quick-start-input--mono {
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}

.quick-start-preview {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.quick-start-preview-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f8fafc;
}

.quick-start-preview-card code {
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #111827;
}

@media (max-width: 900px) {
  .quick-start-header,
  .quick-start-footer {
    flex-direction: column;
  }

  .quick-start-grid,
  .quick-start-links,
  .quick-start-preview {
    grid-template-columns: 1fr;
  }
}
</style>
